<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="flex justify-between items-start">
            <div>
              <h3 class="text-xl font-bold text-gray-900">
                {{ getDocumentName() || 'Revenue Forecast Template' }}
              </h3>
              <p class="text-sm text-gray-500 mt-1">{{ getDocumentDescription() || 'Template for forecasting and planning purposes' }}</p>
            </div>

            <button @click="close" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Template Information -->
            <div>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-gray-500">Document:</span>
                  <span>{{ getDocumentName() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Process:</span>
                  <span>{{ getProcessName() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Status:</span>
                  <span class="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">{{ getDocumentStatus() }}</span>
                </div>
              </div>
            </div>
            
            <!-- Planner Information -->
            <div>
              <div v-if="hasPlanners" class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Assigned Planner:</span>
                  <select
                    v-model="selectedPlannerId"
                    @change="handlePlannerChange"
                    class="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-1.5"
                  >
                    <option v-for="planner in planners" :key="planner.id" :value="planner.id">
                      {{ planner.name || planner.email }}
                    </option>
                  </select>
                </div>
                <div class="flex items-center mt-2">
                  <div v-if="getPlannerAvatarUrl()" class="h-10 w-10 rounded-full flex-shrink-0 overflow-hidden">
                    <img :src="getPlannerAvatarUrl()" alt="Planner Avatar" class="w-full h-full object-cover" />
                  </div>
                  <div v-else class="h-10 w-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                    <span class="text-gray-500 text-sm font-medium">{{ getPlannerInitial() }}</span>
                  </div>
                  <div class="ml-3">
                    <p class="font-medium">{{ getPlannerName() }}</p>
                    <p class="text-sm text-gray-500">{{ getSelectedPlannerEmail() }}</p>
                  </div>
                </div>
              </div>
              <div v-else class="flex items-center">
                <div v-if="getPlannerAvatarUrl()" class="h-10 w-10 rounded-full flex-shrink-0 overflow-hidden">
                  <img :src="getPlannerAvatarUrl()" alt="Planner Avatar" class="w-full h-full object-cover" />
                </div>
                <div v-else class="h-10 w-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                  <span class="text-gray-500 text-sm font-medium">{{ getPlannerInitial() }}</span>
                </div>
                <div class="ml-3">
                  <p class="font-medium">{{ getPlannerName() }}</p>
                  <p class="text-sm text-gray-500">Assigned Planner</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Email Template Preview -->
          <div class="mt-8">
            <div class="flex justify-between items-center mb-4">
              <div class="flex items-center">
                <h4 class="font-medium">Email Template Preview</h4>
                <span
                  class="ml-2 text-xs px-2 py-0.5 rounded-full"
                  :class="isEditMode ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'"
                >
                  {{ isEditMode ? 'Editable' : 'Read-only' }}
                </span>
              </div>
              <button
                @click="toggleEditMode"
                class="text-sm px-3 py-1 rounded-md border"
                :class="isEditMode
                  ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                  : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'"
              >
                {{ isEditMode ? 'Disable Editing' : 'Enable Editing' }}
              </button>
            </div>
            
            <!-- Loading and error states -->
            <div v-if="isLoading" class="flex justify-center items-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span class="ml-2">Loading template...</span>
            </div>
            
            <!-- Template Selection UI -->
            <div v-else-if="showTemplateSelection" class="py-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Select Email Template</h3>
              <div class="grid grid-cols-1 gap-4">
                <div
                  v-for="template in availableTemplates"
                  :key="template.email_template_id"
                  class="border border-gray-200 rounded-lg p-4 hover:bg-blue-50 cursor-pointer transition-colors"
                  @click="selectTemplate(template.email_template_id)"
                >
                  <h4 class="font-medium text-blue-600">
                    {{ template.template_name }}
                  </h4>
                  <p class="text-sm text-gray-500 mt-1">
                    {{ template.subject }}
                  </p>
                </div>
              </div>
            </div>
            
            <div v-else-if="errorMessage" class="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm text-red-700">{{ errorMessage }}</p>
                </div>
              </div>
            </div>
            
            <div
              v-else
              class="space-y-4 bg-white border-2 rounded-md p-4 transition-colors duration-200"
              :class="isEditMode ? 'border-blue-300' : 'border-gray-200'"
            >
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="email"
                  v-model="emailData.to"
                  :class="[
                    'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none',
                    isEditMode
                      ? 'border-blue-300 focus:ring-blue-500 focus:border-blue-500 bg-white'
                      : 'border-gray-200 bg-gray-50 cursor-default'
                  ]"
                  :readonly="!isEditMode"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">CC</label>
                <input
                  type="email"
                  v-model="emailData.cc"
                  placeholder="Comma-separated email addresses"
                  :class="[
                    'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none',
                    isEditMode
                      ? 'border-blue-300 focus:ring-blue-500 focus:border-blue-500 bg-white'
                      : 'border-gray-200 bg-gray-50 cursor-default'
                  ]"
                  :readonly="!isEditMode"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  v-model="emailData.subject"
                  :class="[
                    'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none',
                    isEditMode
                      ? 'border-blue-300 focus:ring-blue-500 focus:border-blue-500 bg-white'
                      : 'border-gray-200 bg-gray-50 cursor-default'
                  ]"
                  :readonly="!isEditMode"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Message</label>
                
                <!-- Rich Text Editor Toolbar (only visible in edit mode) -->
                <div v-if="isEditMode" class="border border-gray-300 rounded-t-md bg-gray-50 p-2 flex flex-wrap gap-1">
                  <button
                    @click.prevent="applyFormatting('bold')"
                    class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
                    title="Bold"
                  >
                    <span class="font-bold text-lg">B</span>
                  </button>
                  <button
                    @click.prevent="applyFormatting('italic')"
                    class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
                    title="Italic"
                  >
                    <span class="italic text-lg">i</span>
                  </button>
                  <button
                    @click.prevent="applyFormatting('underline')"
                    class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
                    title="Underline"
                  >
                    <span class="underline text-lg">U</span>
                  </button>
                </div>
                
                <!-- HTML content display when in readonly mode -->
                <div 
                  v-if="!isEditMode"
                  class="prose max-w-none w-full px-3 py-2 border rounded-md shadow-sm overflow-auto border-gray-200 bg-gray-50 cursor-default h-64"
                  v-html="sanitizedMessageHtml"
                ></div>
                
                <!-- Rich Text Editor Content Area when in edit mode -->
                <div
                  v-else
                  id="email-content-editor"
                  contenteditable="true"
                  @input="updateEmailContent"
                  class="w-full min-h-[250px] px-3 py-2 border border-gray-300 rounded-b-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                ></div>
              </div>
              <!-- File Attachment Section -->
              <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
                <div class="flex flex-col space-y-2">
                  <!-- File input -->
                  <div class="flex items-center">
                    <input
                      type="file"
                      id="file-attachment"
                      @change="handleFileAttachment"
                      class="hidden"
                    />
                    <label
                      for="file-attachment"
                      class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      Add Attachment
                    </label>
                  </div>
                  
                  <!-- Display attached files -->
                  <div v-if="attachments.length > 0" class="mt-2">
                    <div v-for="(attachment, index) in attachments" :key="index" class="flex items-center justify-between bg-gray-50 p-2 rounded-md mb-1">
                      <div class="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span class="text-sm truncate max-w-xs">{{ attachment.filename }}</span>
                      </div>
                      <button
                        @click="removeAttachment(index)"
                        class="text-red-500 hover:text-red-700"
                        title="Remove attachment"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between">
          <div>
            <button
              @click="navigateToEditTemplate"
              class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
            >
              Edit Template
            </button>
          </div>
          <button
            @click="sendEmail"
            :disabled="isLoading"
            class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isLoading ? 'Sending...' : 'Send Email' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, onMounted, computed, nextTick, watch } from 'vue';
import { supabase } from '@/services/supabase';
import { logger } from '@/services/logger';
import { emailService, EmailAttachment } from '@/services/emailService';

interface EmailTemplate {
  id: string; // UUID is stored as string in JavaScript
  subject: string;
  body: string;
  name: string;
}

interface ProcessStep {
  id: string | null; // Can be null for process steps without IDs
  name: string;
  description: string;
  order_number: number;
}

export default defineComponent({
  name: 'EmailReviewModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    processStepType: {
      type: String,
      required: false,
      default: null
    },
    recipientEmail: {
      type: String,
      required: false,
      default: ''
    },
    templateVariables: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  emits: ['close', 'send-success'],
  setup(props, { emit }) {
    const isEditMode = ref(false);
    const isLoading = ref(true);
    const errorMessage = ref('');
    const currentTemplate = ref<EmailTemplate | null>(null);
    const currentProcessStep = ref<ProcessStep | null>(null);
    // Track if the content is HTML - assume HTML by default
    const isHtmlContent = ref(true);
    // Track available templates and template selection UI state
    const availableTemplates = ref<Array<{email_template_id: string, template_name?: string, subject?: string}>>([]);
    const showTemplateSelection = ref(false);
    
    // Track file attachments
    const attachments = ref<EmailAttachment[]>([]);
    
    // Track planners from template variables
    const planners = computed(() => props.templateVariables?.planners || []);
    const hasPlanners = computed(() => planners.value.length > 0);
    const selectedPlannerId = ref('');
    
    // Initialize selectedPlannerId when planners change
    watch(planners, (newPlanners) => {
      if (newPlanners.length > 0 && !selectedPlannerId.value) {
        console.log('Planners changed, setting selectedPlannerId to first planner:', newPlanners[0].id);
        selectedPlannerId.value = newPlanners[0].id;
        // Call handlePlannerChange to update email and planner info
        handlePlannerChange();
      }
    }, { immediate: true });
    
    // Local copies of template variables to avoid mutating props
    const localPlannerName = ref('');
    const localPlannerAvatarUrl = ref('');
    
    const emailData = ref({
      to: '',
      cc: '',
      subject: '',
      message: ''
    });
    
    // Handle file attachment
    const handleFileAttachment = (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (!input.files || input.files.length === 0) return;
      
      const file = input.files[0];
      const reader = new FileReader();
      
      reader.onload = () => {
        // Get base64 content without the prefix (e.g., "data:application/pdf;base64,")
        const base64Content = reader.result as string;
        const contentType = file.type;
        const base64Data = base64Content.split(',')[1];
        
        // Add the attachment
        attachments.value.push({
          filename: file.name,
          content: base64Data,
          encoding: 'base64',
          contentType: contentType
        });
        
        // Reset the file input
        input.value = '';
      };
      
      reader.readAsDataURL(file);
    };
    
    // Remove attachment
    const removeAttachment = (index: number) => {
      attachments.value.splice(index, 1);
    };
    
    // Set the recipient email when the component is created
    onMounted(() => {
      emailData.value.to = props.recipientEmail;
      console.log('Initial recipient email set to:', emailData.value.to);
      
      // If we have planners, set the selected planner to the first one and update planner info
      if (hasPlanners.value && planners.value.length > 0) {
        selectedPlannerId.value = planners.value[0].id;
        
        // Call handlePlannerChange to update email and planner info
        const selectedPlanner = planners.value[0];
        if (selectedPlanner) {
          emailData.value.to = selectedPlanner.email || '';
          localPlannerName.value = selectedPlanner.name || '';
          localPlannerAvatarUrl.value = selectedPlanner.avatar_url || '';
          
          console.log('Initialized with planner:', selectedPlanner.name, 'Email:', selectedPlanner.email);
        }
      } else {
        // Initialize local planner info from props if no planners available
        if (props.templateVariables) {
          localPlannerName.value = props.templateVariables.plannerName || '';
          localPlannerAvatarUrl.value = props.templateVariables.plannerAvatarUrl || props.templateVariables.avatarUrl || '';
        }
      }
    });
    
    // Handle planner selection change
    const handlePlannerChange = () => {
      const selectedPlanner = planners.value.find(p => p.id === selectedPlannerId.value);
      if (selectedPlanner) {
        // Update recipient email
        emailData.value.to = selectedPlanner.email || '';
        
        // Update local planner info
        localPlannerName.value = selectedPlanner.name || '';
        localPlannerAvatarUrl.value = selectedPlanner.avatar_url || '';
      }
    };
    
    // Get the selected planner's email
    const getSelectedPlannerEmail = () => {
      if (hasPlanners.value) {
        const selectedPlanner = planners.value.find(p => p.id === selectedPlannerId.value);
        return selectedPlanner?.email || '';
      }
      return '';
    };
    
    // Main function to load the appropriate template based on the process step
    async function loadTemplateForProcessStep() {
      isLoading.value = true;
      errorMessage.value = '';
      
      try {
        if (!props.processStepType) {
          // If no process step type is provided, load a default template
          setDefaultTemplate();
          return;
        }
        
        // Set current process step
        setCurrentProcessStep();
        
        // If this is a DOWNLOAD process step (reminder), pre-load HEAD_OF_PCO emails into CC field
        if (props.processStepType === 'DOWNLOAD') {
          const headOfPcoEmails = await fetchHeadOfPcoEmails();
          if (headOfPcoEmails) {
            emailData.value.cc = headOfPcoEmails;
            console.log('Pre-loaded HEAD_OF_PCO emails into CC field:', headOfPcoEmails);
          }
        }
        
        // Get available templates for this process step
        const mappings = await getTemplateMappings();
        if (!mappings || mappings.length === 0) {
          return;
        }
        
        // Store available templates
        availableTemplates.value = mappings;
        
        if (mappings.length === 1) {
          // If only one template is available, use it automatically
          await selectTemplate(mappings[0].email_template_id);
        } else {
          // If multiple templates are available, show selection UI
          showTemplateSelection.value = true;
        }
      } catch (error: any) {
        handleTemplateError(error);
      } finally {
        isLoading.value = false;
      }
    }
    
    // Set the current process step
    function setCurrentProcessStep() {
      currentProcessStep.value = {
        id: null,
        name: props.processStepType,
        description: '',
        order_number: 0
      };
    }
    
    // Get all template mappings for the current process step
    async function getTemplateMappings(): Promise<any[]> {
      const { data: mappings, error: mappingError } = await supabase
        .from('email_template_process_mappings')
        .select('email_template_id')
        .eq('process_step_type', props.processStepType)
        .eq('is_active', true);
        
      if (mappingError) {
        throw new Error(`Error fetching template mappings: ${mappingError.message}`);
      }
      
      if (!mappings || mappings.length === 0) {
        // No mappings found, use default template
        logger.info(`No template mappings found for process step ${props.processStepType}, using default`);
        setDefaultTemplate();
        return [];
      }
      
      // Fetch template details for each mapping
      const templatesWithDetails = await Promise.all(
        mappings.map(async (mapping) => {
          try {
            const { data: template } = await supabase
              .from('email_templates')
              .select('id, template_name, subject')
              .eq('id', mapping.email_template_id)
              .single();
              
            return {
              email_template_id: mapping.email_template_id,
              template_name: template?.template_name || `Template ${mapping.email_template_id.substring(0, 8)}`,
              subject: template?.subject || 'No subject'
            };
          } catch (error) {
            console.error('Error fetching template details:', error);
            return {
              email_template_id: mapping.email_template_id,
              template_name: `Template ${mapping.email_template_id.substring(0, 8)}`,
              subject: 'No details available'
            };
          }
        })
      );
      
      return templatesWithDetails;
    }
    
    // Select a specific template from the available options
    async function selectTemplate(templateId: string) {
      try {
        isLoading.value = true;
        
        const template = await getEmailTemplate(templateId);
        if (!template) {
          return;
        }
        
        currentTemplate.value = template;
        processTemplateContentWithVariables(template);
        
        // Hide template selection UI
        showTemplateSelection.value = false;
      } catch (error: any) {
        handleTemplateError(error);
      } finally {
        isLoading.value = false;
      }
    }
    
    // Get the email template by ID
    async function getEmailTemplate(templateId: string): Promise<any> {
      const { data: template, error: templateError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', templateId)
        .single();
        
      if (templateError) {
        throw new Error(`Error fetching email template: ${templateError.message}`);
      }
      
      if (!template) {
        throw new Error(`Email template with ID ${templateId} not found`);
      }
      
      currentTemplate.value = template;
      return template;
    }
    
    // Process the template content
    function processTemplateContentWithVariables(template: any): void {
      // Log the template data for debugging
      console.log('Template loaded:', template);
      console.log('Template keys:', Object.keys(template));
      console.log('Template subject:', template.subject);
      
      // Log template variables for debugging
      console.log('Template variables:', props.templateVariables);
      console.log('Has avatar URL:', !!props.templateVariables.avatarUrl);
      console.log('Avatar URL:', props.templateVariables.avatarUrl);
      
      // Extract message content
      const messageContent = extractMessageContent(template);
      
      let processedSubject = template.subject || '';
      let processedContent = messageContent || '';
      
      // Replace variables in subject and content
      if (props.templateVariables && Object.keys(props.templateVariables).length > 0) {
        console.log('Processing template with variables:', props.templateVariables);
        
        processedSubject = replaceVariables(processedSubject, props.templateVariables);
        processedContent = replaceVariables(processedContent, props.templateVariables);
      }
      
      setEmailData({
        ...template,
        subject: processedSubject
      }, processedContent);
    }
    
    // Extract message content from template
    function extractMessageContent(template: any): string {
      // Check for different possible column names for the message body
      const messageContent = template.body || template.content || template.message || template.text || '';
      console.log('Message content found:', messageContent);
      console.log('Message content type:', typeof messageContent);
      
      // Try to parse JSON content
      if (typeof messageContent === 'string' && messageContent.trim().startsWith('{')) {
        const jsonContent = tryParseJsonContent(messageContent);
        if (jsonContent) {
          return jsonContent;
        }
      }
      
      // Check if content is HTML
      checkIfContentIsHtml(messageContent);
      
      return messageContent;
    }
    
    // Try to parse JSON content
    function tryParseJsonContent(content: string): string | null {
      try {
        const parsedContent = JSON.parse(content);
        console.log('Parsed JSON content:', parsedContent);
        
        if (parsedContent.html) {
          console.log('Found HTML content in JSON:', parsedContent.html);
          emailData.value.message = parsedContent.html;
          isHtmlContent.value = true;
          return parsedContent.html;
        }
      } catch (e) {
        console.log('Not valid JSON, using as is', e);
      }
      
      return null;
    }
    
    // Check if content is HTML
    function checkIfContentIsHtml(content: string): void {
      if (typeof content === 'string' &&
          (content.includes('<html') ||
           content.includes('<body') ||
           content.includes('<div') ||
           content.includes('<p'))) {
        console.log('Content appears to be HTML');
        isHtmlContent.value = true;
      }
    }
    
    // Set email data from template and content
    function setEmailData(template: any, messageContent: string | null): void {
      // Set subject
      emailData.value.subject = template.subject || 'No subject found';
      
      // Set message content
      if (messageContent) {
        emailData.value.message = messageContent;
      } else {
        console.warn('No message content found in template, using fallback');
        setFallbackMessage();
      }
    }
    
    // Set fallback message
    function setFallbackMessage() {
      emailData.value.message = `Dear ${props.recipientEmail.split('@')[0]},

This is a fallback message because the template content could not be loaded.

Please check the template configuration in the database.

Best regards,
Financial Control Team`;
    }
    
    // Handle template loading error
    function handleTemplateError(error: any): void {
      logger.error('Error loading template for process step', { error });
      errorMessage.value = error?.message || 'Failed to load email template';
      setDefaultTemplate();
    }
    
    function setDefaultTemplate() {
      // Use template variables if available
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const firstName = getFirstName();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const lastName = getLastName();
      const fullName = getFullName();
      const period = getForecastPeriod();
      const dueDate = getDueDate();
      const companyName = getCompanyName();
      
      emailData.value = {
        to: props.recipientEmail || 'andrej.svetina@fraport-slovenija.si',
        cc: '',
        subject: `Revenue Forecast Template for ${period}`,
        message: `Dear ${fullName},

Attached is the Revenue Forecast Template for ${period}. Please complete this template with your forecasted data by ${dueDate}.

If you have any questions, please don't hesitate to contact the Financial Control team.

Best regards,
Financial Control Team
${companyName}`
      };
    }
    
    // Load the template when the component is mounted or when props change
    onMounted(() => {
      loadTemplateForProcessStep();
      
      // Initialize the dropdown with the first planner after a short delay
      // to ensure the template variables are fully loaded
      setTimeout(() => {
        if (hasPlanners.value && planners.value.length > 0) {
          console.log('Setting selectedPlannerId in setTimeout to:', planners.value[0].id);
          console.log('Available planners:', planners.value.map(p => ({ id: p.id, name: p.name })));
          selectedPlannerId.value = planners.value[0].id;
          handlePlannerChange();
        }
      }, 300);
    });
    
    // Watch for isOpen changes to initialize the dropdown when the modal is opened
    watch(() => props.isOpen, (isOpen) => {
      if (isOpen && hasPlanners.value && planners.value.length > 0) {
        console.log('Modal opened, setting selectedPlannerId to first planner');
        selectedPlannerId.value = planners.value[0].id;
        handlePlannerChange();
      }
    });
    
    // Watch for changes in the template variables
    watch(() => props.templateVariables, (newVars, oldVars) => {
      if (JSON.stringify(newVars) !== JSON.stringify(oldVars)) {
        console.log('Template variables changed, reloading template');
        loadTemplateForProcessStep();
      }
    }, { deep: true });
    
    // Watch for changes in the recipient email
    watch(() => props.recipientEmail, (newEmail, oldEmail) => {
      if (newEmail !== oldEmail) {
        emailData.value.to = newEmail;
      }
    });
    
    function close() {
      emit('close');
    }
    
    function navigateToEditTemplate() {
      close(); // Close the modal first
      // Use window.location to navigate
      window.location.href = '/administration/email-templates';
    }
    
    function toggleEditMode() {
      isEditMode.value = !isEditMode.value;
      
      // When switching to edit mode, initialize the rich text editor
      if (isEditMode.value) {
        nextTick(() => {
          const editorElement = document.getElementById('email-content-editor');
          if (editorElement) {
            editorElement.innerHTML = emailData.value.message;
          }
        });
      }
    }
    
    // Update email content from the rich text editor
    function updateEmailContent(event: Event) {
      const target = event.target as HTMLElement;
      emailData.value.message = target.innerHTML;
    }
    
    // Apply formatting to the rich text editor
    function applyFormatting(format: string) {
      document.execCommand('styleWithCSS', false, 'true');
      
      switch (format) {
        case 'bold':
          document.execCommand('bold', false);
          break;
        case 'italic':
          document.execCommand('italic', false);
          break;
        case 'underline':
          document.execCommand('underline', false);
          break;
      }
      
      // Update the email content after formatting
      const editorElement = document.getElementById('email-content-editor');
      if (editorElement) {
        emailData.value.message = editorElement.innerHTML;
      }
    }

    async function sendEmail() {
      isLoading.value = true;
      errorMessage.value = '';
      
      try {
        logger.info('Sending email', {
          recipients: emailData.value.to,
          subject: emailData.value.subject
        });
        
        const headOfPcoEmails = await fetchHeadOfPcoEmails();

        // Prepare CC recipients - combine user-entered CC with HEAD_OF_PCO emails for reminders
        let ccRecipients = emailData.value.cc;
        if (headOfPcoEmails) {
          ccRecipients = ccRecipients ? `${ccRecipients},${headOfPcoEmails}` : headOfPcoEmails;
        }
        
        // Use the centralized email service with BCC for all cases
        const result = await emailService.sendEmail({
          bcc: emailData.value.to,
          cc: ccRecipients,
          subject: emailData.value.subject,
          body: sanitizedMessageHtml.value,
          attachments: attachments.value.length > 0 ? attachments.value : undefined
        });
            
            if (!result.success) {
              throw new Error(result.message || 'Failed to send email');
            }
            
            logger.info('Email sent successfully', { data: result.data });
            
            // Update document status from CREATING to READY_FOR_DOWNLOAD
            if (props.templateVariables?.documentId) {
              try {
                const { error: updateError } = await supabase
                  .from('documents')
                  .update({
                    status: 'READY_FOR_DOWNLOAD',
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', props.templateVariables.documentId);
                
                if (updateError) {
                  logger.error('Error updating document status', { error: updateError });
                  console.error('Failed to update document status:', updateError);
                } else {
                  logger.info('Document status updated to READY_FOR_DOWNLOAD', {
                    documentId: props.templateVariables.documentId
                  });
                }
              } catch (updateError) {
                logger.error('Exception updating document status', { error: updateError });
                console.error('Exception updating document status:', updateError);
                // Continue with the flow even if status update fails
              }
            } else {
              logger.warn('No document ID provided, skipping status update');
            }
            
            // Emit success event
            emit('send-success');
            
            // Close the modal
            close();
          } catch (error: any) {
            logger.error('Error sending email', { error });
            errorMessage.value = error?.message || 'Failed to send email. Please try again.';
          } finally {
            isLoading.value = false;
          }
        }
    
    // Computed property to determine if content has HTML tags
    const contentHasHtmlTags = computed(() => {
      if (!emailData.value || !emailData.value.message) {
        return false;
      }
      
      const content = emailData.value.message;
      return content.includes('<html') || 
             content.includes('<body') || 
             content.includes('<div') || 
             content.includes('<p') ||
             content.includes('<span') ||
             content.includes('<br');
    });
    
    // Computed property to sanitize HTML content
    const sanitizedMessageHtml = computed(() => {
      // In a production environment, you should use a proper HTML sanitizer like DOMPurify
      // This is a simple implementation for demonstration purposes
      if (!emailData.value || !emailData.value.message) {
        return ''; // Return empty string if message is undefined
      }
      
      const content = emailData.value.message;
      
      // If content has HTML tags or is marked as HTML, return as is
      if (isHtmlContent.value || contentHasHtmlTags.value) {
        return content;
      }
      
      // Otherwise, convert newlines to <br> tags
      return content
        .replace(/\n/g, '<br>')
        .trim();
    });
    
    function replaceVariables(text: string, variables: Record<string, any>): string {
      if (!text) return '';
      
      return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
        const trimmedName = variableName.trim();
        
        if (Object.prototype.hasOwnProperty.call(variables, trimmedName)) {
          const value = variables[trimmedName];
          return value !== null && value !== undefined ? String(value) : '';
        }
        
        console.warn(`Variable not found: ${trimmedName}`);
        return match;
      });
    }

    // Helper functions to get template variables with fallbacks
    function getFirstName(): string {
      return props.templateVariables?.firstName || 'Andrej';
    }
    
    function getLastName(): string {
      return props.templateVariables?.lastName || 'Svetina';
    }
    
    function getFullName(): string {
      return props.templateVariables?.fullName || `${getFirstName()} ${getLastName()}`;
    }
    
    function getForecastPeriod(): string {
      return props.templateVariables?.forecastPeriod || 'Q2 2025';
    }
    
    function getDocumentName(): string {
      return props.templateVariables?.documentName || 'Revenue Forecast Template';
    }
    
    function getDocumentDescription(): string {
      return props.templateVariables?.documentDescription || '';
    }
    
    function getDocumentStatus(): string {
      return props.templateVariables?.documentStatus || 'Email Pending';
    }
    
    function getProcessName(): string {
      return props.templateVariables?.processName || 'Forecasting Process';
    }
    
    function getDueDate(): string {
      return props.templateVariables?.dueDate || 'April 20, 2025';
    }
    
    function getCompanyName(): string {
      return props.templateVariables?.companyName || 'Fraport AG';
    }
    
    function getPlannerName(): string {
      if (hasPlanners.value) {
        const selectedPlanner = planners.value.find(p => p.id === selectedPlannerId.value);
        return selectedPlanner?.name || localPlannerName.value || getFullName();
      }
      return localPlannerName.value || getFullName();
    }
    
    function getPlannerAvatarUrl(): string {
      if (hasPlanners.value) {
        const selectedPlanner = planners.value.find(p => p.id === selectedPlannerId.value);
        return selectedPlanner?.avatar_url || localPlannerAvatarUrl.value || '';
      }
      return localPlannerAvatarUrl.value || '';
    }
    
    function getPlannerInitial(): string {
      const name = getPlannerName();
      return name ? name.charAt(0).toUpperCase() : '?';
    }
    
    async function fetchHeadOfPcoEmails(): Promise<string> {
      // Only fetch HEAD_OF_PCO emails for DOWNLOAD process step type (reminders)
      if (props.processStepType !== 'DOWNLOAD') return '';
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('email')
          .eq('business_role', 'HEAD_OF_PCO');
        
        if (error) {
          console.error('Error fetching HEAD_OF_PCO emails:', error);
          return '';
        }
        
        if (!data || data.length === 0) {
          console.log('No HEAD_OF_PCO users found');
          return '';
        }
        
        const emails = data.map(profile => profile.email).filter(Boolean);
        return emails.join(',');
      } catch (err) {
        console.error('Exception fetching HEAD_OF_PCO emails:', err);
        return '';
      }
    }

    return {
      close,
      navigateToEditTemplate,
      isEditMode,
      isLoading,
      errorMessage,
      emailData,
      toggleEditMode,
      sendEmail,
      currentTemplate,
      currentProcessStep,
      sanitizedMessageHtml,
      isHtmlContent,
      contentHasHtmlTags,
      availableTemplates,
      showTemplateSelection,
      selectTemplate,
      updateEmailContent,
      applyFormatting,
      // Attachment handling
      attachments,
      handleFileAttachment,
      removeAttachment,
      // Planner handling
      planners,
      hasPlanners,
      selectedPlannerId,
      handlePlannerChange,
      getSelectedPlannerEmail,
      // Expose helper functions for the template
      getFirstName,
      getLastName,
      getFullName,
      getForecastPeriod,
      getDocumentName,
      getDocumentDescription,
      getDocumentStatus,
      getProcessName,
      getDueDate,
      getCompanyName,
      getPlannerName,
      getPlannerAvatarUrl,
      getPlannerInitial
    };
  }
});
</script>

<style scoped>
.prose {
  line-height: 1.6;
}
</style>
